import React, { useEffect, useState } from 'react';
import { Icon, Note } from '@repo/design-system';

/**
 * How long after its region mounts the late-refusal line is put in it. A polite region announces a
 * CHANGE: one that mounts already holding its text says nothing, and the summary and the start
 * screen each mount with the line in hand. The pause lets the empty region reach the accessibility
 * tree before the text does.
 */
export const LATE_REFUSAL_FILL_MS = 100;

/**
 * The polite region that carries R12's late-refusal line (`equipment-page-reimport`, R12, R14) —
 * on the summary and on the start screen an upload that wrote nothing returns to. Always mounted
 * with its host; the visible Note is filled a moment after mount, so it is announced. `children`
 * share the region (the summary's *Kept {name}* announcements).
 */
export const LateRefusalStatus: React.FC<{ line: string | null; children?: React.ReactNode }> = ({
  line,
  children,
}) => {
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    if (!line) {
      setSaid(null);
      return undefined;
    }
    const timer = window.setTimeout(() => setSaid(line), LATE_REFUSAL_FILL_MS);
    return () => window.clearTimeout(timer);
  }, [line]);
  return (
    <div role="status" aria-live="polite">
      {said && (
        <Note tone="butter" icon={<Icon name="triangle-exclamation" size="xs" />}>
          {said}
        </Note>
      )}
      {children}
    </div>
  );
};

export default LateRefusalStatus;
